/**
 * Life4Earth V2 — Chronicles API adapter
 *
 * CRUD operations for Chronicles and the user-chronicles endpoint
 * used by the Impact Signature page.
 *
 * Ownership model:
 *   - initiatorGuid:  who gets impact credit
 *   - createdByGuid:  audit trail (session user who created it)
 *   - ownerGuid:      user or community whose page shows it
 *   - ownerType:      'user' | 'community'
 */

import { apiFetch } from "./client";

// ── Types ────────────────────────────────────────────────────

export interface ChronicleSummary {
  chronicleGuid: string;
  initiatorGuid: string;
  initiatorDisplayName: string | null;
  ownerGuid: string;
  ownerType: string;
  ownerDisplayName: string | null;
  title: string;
  subtitle: string | null;
  heroImageUrl: string | null;
  heroOriginalUrl: string | null;
  categoryKey: string;
  dimensionTags: string[];
  outcomeAssessment: string | null;
  status: string;
  /** public | community | private (DEV-1381). Present so listings can show the author who can see each post. */
  visibility: string;
  /** Content type (DEV-1392) — e.g. 'journal_entry'. Null on legacy rows created before migration 255. */
  templateKey: string | null;
  createdAt: string;
  updatedAt: string;
  summaryEvaluation: string | null;
}

export interface ChronicleTeamCreditResponse {
  creditGuid: string;
  personName: string;
  roleTitle: string | null;
  description: string | null;
  userGuid: string | null;
  externalProfileUrl: string | null;
  manualPhotoUrl: string | null;
  sortOrder: number;
}

export interface ChronicleTimelineEventResponse {
  eventGuid: string;
  dateLabel: string;
  eventDate: string | null;
  eventTitle: string;
  description: string | null;
  eventTime: string | null;
  eventStatus: string;
  address: string | null;
  sortOrder: number;
}

export interface ChronicleExternalLinkResponse {
  linkGuid: string;
  url: string;
  label: string;
  linkType: string;
  sortOrder: number;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  ogSiteName?: string;
  ogPublishedAt?: string;
  ogAuthor?: string;
}

export interface ChronicleOutcomeMetricResponse {
  metricGuid: string;
  label: string;
  targetValue: number | null;
  actualValue: number | null;
  unit: string | null;
  sortOrder: number;
}

export interface ChronicleMediaResponse {
  mediaGuid: string;
  fileUrl: string;
  mediaType: string;
  caption: string | null;
  creditSource: string | null;
  /** hero | gallery | inline (236) */
  role: string;
  /** Original media URL for provenance/credit; present for link-only and re-hosted media (236). */
  sourceMediaUrl: string | null;
  sourceGuid: string | null;
  sortOrder: number;
}

// ── Attributed & Researched (236, DEV-1314) ──────────────────

export interface ChronicleSourceResponse {
  sourceGuid: string;
  /** primary | reference */
  sourceRole: string;
  sourceUrl: string | null;
  previousSourceUrl: string | null;
  sourceOrgName: string | null;
  sourceTitle: string | null;
  /** linked_reference | permission_requested | granted | declined */
  permissionStatus: string;
  permissionNote: string | null;
  lastFetchedAt: string | null;
  /** ok | error | stale | changed */
  fetchStatus: string | null;
  position: number;
}

export interface ChronicleExemplarLinkResponse {
  exemplarLinkGuid: string;
  /** workspace | solution */
  targetType: string;
  targetGuid: string;
  note: string | null;
}

/** Shared discovery classification (chronicle-scoped here; §4.8). */
export interface EntityClassificationResponse {
  classificationGuid: string;
  /** solution | problem | issue | outcome */
  dimension: string;
  targetGuid: string | null;
  valueKey: string | null;
  note: string | null;
  position: number;
}

/** Shared local-conditions tag (chronicle-scoped here; §3a/§4.8). */
export interface EntityContextResponse {
  contextGuid: string;
  /** region | climate | urban_rural | scale | demographics | other */
  contextKey: string;
  contextValue: string;
  isAuto: boolean;
  note: string | null;
  position: number;
}

/**
 * Per-tier reach state for the author's reach-ladder view (DEV-1402, §12.1).
 * Computed server-side — the ladder reads these strings, never re-derives reach.
 */
export interface ChronicleReach {
  /** The Gate-1 target community this content is associated with, or null. */
  communityGuid: string | null;
  /** Resolved community name when communityGuid is set. */
  communityName: string | null;
  /**
   * Community-feed (Gate-1) reach:
   * none | eligible | pending | reaching | suspended
   */
  communityFeedStatus: 'none' | 'eligible' | 'pending' | 'reaching' | 'suspended';
  /**
   * Search-and-discovery (Gate-2/expert) reach:
   * not_submitted | pending | changes_requested | reaching | suspended
   */
  searchStatus: 'not_submitted' | 'pending' | 'changes_requested' | 'reaching' | 'suspended';
}

export interface ChronicleResponse extends ChronicleSummary {
  createdByGuid: string;
  communityGuid: string | null;
  missionNarrative: string;
  background: string | null;
  geographyGuid: string | null;
  locationLat: number | null;
  locationLon: number | null;
  /** Free-text place when geography_guid does not resolve to a geo community (236). */
  locationPlaceText: string | null;
  /** native | attributed (236). */
  sourceType: string;
  /** public | community | private (239). */
  visibility: string;
  /** AI-seeded draft (243, DEV-1354). */
  aiGenerated?: boolean;
  /** Review lifecycle state (DEV-1398): draft | submitted | in_review | changes_requested | reviewed | rejected. Controls *reach*, orthogonal to status. */
  reviewStatus: string;
  /** Derived review bar: light | standard | editorial | scholarly. 'light' self-publishes and never enters the review pool. */
  reviewModel: string;
  /**
   * True when validation is paused because the reviewed core was edited after
   * validation (DEV-1400, §12.11). Reach requires reviewStatus==='reviewed' AND
   * NOT validationSuspended. Content stays on the author's page + followers;
   * resubmit re-enters the pool to restore reach.
   */
  validationSuspended: boolean;
  calculatedSummary: string | null;
  teamCredits: ChronicleTeamCreditResponse[];
  timelineEvents: ChronicleTimelineEventResponse[];
  externalLinks: ChronicleExternalLinkResponse[];
  outcomeMetrics: ChronicleOutcomeMetricResponse[];
  media: ChronicleMediaResponse[];
  sources: ChronicleSourceResponse[];
  exemplarLinks: ChronicleExemplarLinkResponse[];
  classifications: EntityClassificationResponse[];
  contexts: EntityContextResponse[];
  /**
   * Author-declared SUBJECT tags (DEV-1396) — platform_skills.slug values with
   * resolved labels, direct-matched to reviewer endorsements for Gate-2 routing.
   * Edited via setChronicleSubjects; required before submit-for-review (Gate 2).
   */
  subjects: ChronicleSubjectResponse[];
  /** Per-tier reach state (DEV-1402). Present on the single-chronicle GET; null on list responses. */
  reach?: ChronicleReach | null;
}

/** One author-declared subject tag (DEV-1396). skillSlug = platform_skills.slug. */
export interface ChronicleSubjectResponse {
  skillSlug: string;
  label: string;
}

export interface ChronicleListResult {
  items: ChronicleSummary[];
  total: number;
  page: number;
  pageSize: number;
}

// ── Create/Update request types ──────────────────────────────

export interface CreateChronicleRequest {
  title: string;
  subtitle?: string;
  /** Optional (DEV-1336) — new chronicles author their body via content sections, not this legacy field. */
  missionNarrative?: string;
  background?: string;
  heroImageUrl?: string;
  heroOriginalUrl?: string;
  categoryKey: string;
  /**
   * Content-type key (DEV-1392). REQUIRED by the server — it derives the
   * reviewModel bar from this server-side; the client never sends reviewModel.
   * One of ContentTemplateKey from contentTemplates.ts.
   */
  templateKey: string;
  initiatorGuid?: string;
  ownerGuid?: string;
  ownerType?: string;
  geographyGuid?: string;
  locationLat?: string;
  locationLon?: string;
  dimensionTags?: string[];
  outcomeAssessment?: string;
  locationPlaceText?: string;
  /** native | attributed. Setting 'attributed' requires admin/curator (server-enforced, §2). */
  sourceType?: string;
  /**
   * public | community | private. OPTIONAL — omit it and the server defaults by
   * content type (DEV-1381): journal_entry → 'private', everything else → 'public'.
   * Prefer omitting: the safe default is the server's job, not the caller's.
   */
  visibility?: string;
  teamCredits?: UpsertTeamCreditRequest[];
  timelineEvents?: UpsertTimelineEventRequest[];
  externalLinks?: UpsertExternalLinkRequest[];
  outcomeMetrics?: UpsertOutcomeMetricRequest[];
  sources?: UpsertChronicleSourceRequest[];
  exemplarLinks?: UpsertExemplarLinkRequest[];
  classifications?: UpsertClassificationRequest[];
  contexts?: UpsertContextRequest[];
}

export interface UpdateChronicleRequest {
  title?: string;
  subtitle?: string;
  missionNarrative?: string;
  background?: string;
  heroImageUrl?: string;
  heroOriginalUrl?: string;
  categoryKey?: string;
  ownerGuid?: string;
  ownerType?: string;
  geographyGuid?: string;
  locationLat?: string;
  locationLon?: string;
  locationPlaceText?: string;
  dimensionTags?: string[];
  outcomeAssessment?: string;
  status?: string;
  /** native | attributed. Switching to 'attributed' requires admin/curator (server-enforced, §2). */
  sourceType?: string;
  /** public | community | private (239). */
  visibility?: string;
  teamCredits?: UpsertTeamCreditRequest[];
  timelineEvents?: UpsertTimelineEventRequest[];
  externalLinks?: UpsertExternalLinkRequest[];
  outcomeMetrics?: UpsertOutcomeMetricRequest[];
  sources?: UpsertChronicleSourceRequest[];
  exemplarLinks?: UpsertExemplarLinkRequest[];
  classifications?: UpsertClassificationRequest[];
  contexts?: UpsertContextRequest[];
}

export interface UpsertTeamCreditRequest {
  creditGuid?: string;
  personName: string;
  roleTitle?: string;
  description?: string;
  userGuid?: string;
  externalProfileUrl?: string;
  manualPhotoUrl?: string;
  sortOrder: number;
}

export interface UpsertChronicleSourceRequest {
  sourceGuid?: string;
  /** primary | reference */
  sourceRole: string;
  sourceUrl?: string;
  sourceOrgName?: string;
  sourceTitle?: string;
  /** linked_reference | permission_requested | granted | declined */
  permissionStatus?: string;
  permissionNote?: string;
  position: number;
}

export interface UpsertExemplarLinkRequest {
  exemplarLinkGuid?: string;
  /** workspace | solution */
  targetType: string;
  targetGuid: string;
  note?: string;
}

export interface UpsertClassificationRequest {
  classificationGuid?: string;
  /** solution | problem | issue | outcome */
  dimension: string;
  targetGuid?: string;
  valueKey?: string;
  note?: string;
  position: number;
}

export interface UpsertContextRequest {
  contextGuid?: string;
  /** region | climate | urban_rural | scale | demographics | other */
  contextKey: string;
  contextValue: string;
  isAuto?: boolean;
  note?: string;
  position: number;
}

export interface UpsertTimelineEventRequest {
  eventGuid?: string;
  dateLabel: string;
  eventDate?: string;
  eventTitle: string;
  description?: string;
  eventTime?: string;
  eventStatus?: string;
  address?: string;
  sortOrder: number;
}

export interface UpsertExternalLinkRequest {
  linkGuid?: string;
  url: string;
  label: string;
  linkType?: string;
  sortOrder: number;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  ogSiteName?: string;
  ogPublishedAt?: string;
  ogAuthor?: string;
}

export interface UpsertOutcomeMetricRequest {
  metricGuid?: string;
  label: string;
  targetValue?: number;
  actualValue?: number;
  unit?: string;
  sortOrder: number;
}

// ── Proxy types ──────────────────────────────────────────────

export interface ProxyBeneficiarySummary {
  userGuid: string;
  displayName: string;
}

// ── API Calls ────────────────────────────────────────────────

/** Fetch Chronicles for a specific user (Impact Signature). */
export function fetchUserChronicles(userGuid: string): Promise<ChronicleSummary[]> {
  return apiFetch<ChronicleSummary[]>(`/api/chronicles/user/${userGuid}`);
}

/** Fetch Chronicles owned by a specific entity (user or community page). */
export function fetchOwnerChronicles(ownerGuid: string): Promise<ChronicleSummary[]> {
  return apiFetch<ChronicleSummary[]>(`/api/chronicles/owner/${ownerGuid}`);
}

/** Fetch a single Chronicle by GUID. */
export function fetchChronicle(guid: string): Promise<ChronicleResponse> {
  return apiFetch<ChronicleResponse>(`/api/chronicles/${guid}`);
}

/** List Chronicles with optional filters. */
export function fetchChronicles(params?: Record<string, string>): Promise<ChronicleListResult> {
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  return apiFetch<ChronicleListResult>(`/api/chronicles${qs}`);
}

/** Create a new Chronicle draft. */
export function createChronicle(req: CreateChronicleRequest): Promise<ChronicleResponse> {
  return apiFetch<ChronicleResponse>("/api/chronicles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/**
 * Create a personal blog post — the `journal_entry` content type (DEV-1381).
 *
 * The profile Blog tab is a SINGLE-TYPE surface (SPEC_PERSONAL_JOURNAL §12.3):
 * "New Blog" opens the editor directly, with no type picker and no detour through
 * the Knowledge window. This helper is that path.
 *
 * Visibility is intentionally NOT sent — the server defaults journal_entry to
 * 'private' and it must stay the server's decision so it fails closed.
 */
export function createJournalEntry(title: string): Promise<ChronicleResponse> {
  return createChronicle({
    title,
    categoryKey: 'journal_entry',
    templateKey: 'journal_entry',
  });
}

/** Update an existing Chronicle. */
export function updateChronicle(guid: string, req: UpdateChronicleRequest): Promise<ChronicleResponse> {
  return apiFetch<ChronicleResponse>(`/api/chronicles/${guid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/**
 * Set (replace) a Chronicle's author-declared SUBJECT tags (DEV-1396). Author-only.
 * skillKeys are platform_skills slugs — every key must already exist (unknown keys
 * are rejected, never auto-created). Returns the updated subjects with labels.
 */
export function setChronicleSubjects(
  guid: string,
  skillKeys: string[],
): Promise<{ chronicleGuid: string; subjects: ChronicleSubjectResponse[] }> {
  return apiFetch<{ chronicleGuid: string; subjects: ChronicleSubjectResponse[] }>(
    `/api/chronicles/${guid}/subjects`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ skillKeys }),
    },
  );
}

/** Soft-delete a Chronicle (owner). Hides it everywhere; restorable until a
 *  sysadmin hard-deletes it (migration 249). */
export function deleteChronicle(guid: string): Promise<void> {
  return apiFetch<void>(`/api/chronicles/${guid}`, { method: "DELETE" });
}

/** Restore a soft-deleted Chronicle (owner or sysadmin). */
export function restoreChronicle(guid: string): Promise<void> {
  return apiFetch<void>(`/api/chronicles/${guid}/restore`, { method: "POST" });
}

/** Admin-console summary row (all chronicles, including soft-deleted). */
export interface AdminChronicleSummary {
  chronicleGuid: string;
  title: string;
  categoryKey: string;
  status: string;
  ownerType: string;
  ownerDisplayName: string | null;
  initiatorDisplayName: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Sysadmin: list every chronicle (incl. soft-deleted) for the cleanup console. */
export function fetchAdminChronicles(params?: { search?: string; categoryKey?: string }): Promise<AdminChronicleSummary[]> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set("search", params.search);
  if (params?.categoryKey) qs.set("categoryKey", params.categoryKey);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<AdminChronicleSummary[]>(`/api/chronicles/admin/all${suffix}`);
}

/** Sysadmin: permanently hard-delete a Chronicle and all dependent rows. */
export function hardDeleteChronicle(guid: string): Promise<{ message: string; recordsRemoved: Record<string, number> }> {
  return apiFetch<{ message: string; recordsRemoved: Record<string, number> }>(
    `/api/chronicles/${guid}/hard`,
    { method: "DELETE" },
  );
}

/** Add a media item to a Chronicle. */
export function addChronicleMedia(
  chronicleGuid: string,
  req: { fileKey: string; mediaType?: string; caption?: string; creditSource?: string },
): Promise<ChronicleMediaResponse> {
  return apiFetch<ChronicleMediaResponse>(`/api/chronicles/${chronicleGuid}/media`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
}

/** Remove a media item from a Chronicle. */
export function deleteChronicleMedia(
  chronicleGuid: string,
  mediaGuid: string,
): Promise<void> {
  return apiFetch<void>(`/api/chronicles/${chronicleGuid}/media/${mediaGuid}`, {
    method: "DELETE",
  });
}

// ── Share to Chronicle (add news link) ───────────────────────

export interface AddNewsLinkResponse {
  linkGuid: string;
  chronicleGuid: string;
  url: string;
  label: string;
  linkType: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  ogSiteName?: string;
  ogPublishedAt?: string;
  ogAuthor?: string;
}

/** Add a news item as an external link on a chronicle (copies OG metadata server-side). */
export function addNewsLinkToChronicle(
  chronicleGuid: string,
  newsItemGuid: string,
): Promise<AddNewsLinkResponse> {
  return apiFetch<AddNewsLinkResponse>(`/api/chronicles/${chronicleGuid}/add-news-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ newsItemGuid }),
  });
}

/** Fetch users I can create content on behalf of. */
export function fetchProxyBeneficiaries(): Promise<ProxyBeneficiarySummary[]> {
  return apiFetch<ProxyBeneficiarySummary[]>("/api/contentproxies/beneficiaries");
}

// ── Reach ladder — community feed (Gate 1) reach target (DEV-1402) ────

/** A community the caller is an active member of — options for the submit-to-community picker. */
export interface MyCommunityOption {
  communityGuid: string;
  name: string;
}

/** Fetch the caller's active community memberships for the submit-to-community picker. */
export function fetchMyCommunitiesForPicker(): Promise<MyCommunityOption[]> {
  return apiFetch<MyCommunityOption[]>("/api/chronicles/my-communities");
}

/**
 * Submit personal content to a community's feed — the Gate-1 reach-target
 * association (DEV-1402, §12.1). Sets the target community as a REACH TARGET
 * only (does NOT change ownership) and enters that community's Gate-1 pool.
 * Returns the updated reach. Author + active-member of the target enforced server-side.
 */
export function submitChronicleToCommunity(
  chronicleGuid: string,
  communityGuid: string,
): Promise<{ chronicleGuid: string; reach: ChronicleReach }> {
  return apiFetch<{ chronicleGuid: string; reach: ChronicleReach }>(
    `/api/chronicles/${chronicleGuid}/submit-to-community`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ communityGuid }),
    },
  );
}
